import React, { useCallback, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../../admin/welcome/downloadPngDataUrl";
import {
    JIGSAW_MOBILE_TITLE,
} from "../../prototype/mobile-jigsaw/routes";
import { SLIDING_PUZZLE_QUEST_TITLE } from "../../prototype/mobile-sliding/routes";
import { WORD_WHEEL_QUEST_TITLE } from "../../prototype/mobile-word-wheel/routes";
import slidingAppIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";

/**
 * Facebook Page cover — recommended upload 1640×624 (displays ~820×312).
 * Keep key content out of the bottom-left (profile photo overlap on mobile).
 */
export const FACEBOOK_COVER_W = 1640;
export const FACEBOOK_COVER_H = 624;
export const FACEBOOK_COVER_FILE = "puzzle-interact-facebook-cover-1640x624.png";

const PUBLIC = process.env.PUBLIC_URL || "";
const JIGSAW_APP_ICON = `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`;
const WORD_WHEEL_APP_ICON = `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`;
const PROMO_IMAGE = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const BRAND_STACK =
    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

const PUBLISHED_APPS = [
    {
        key: "sliding",
        label: "Sliding",
        title: SLIDING_PUZZLE_QUEST_TITLE,
        iconSrc: slidingAppIcon,
    },
    {
        key: "jigsaw",
        label: "Jigsaw",
        title: JIGSAW_MOBILE_TITLE,
        iconSrc: JIGSAW_APP_ICON,
    },
    {
        key: "word-wheel",
        label: "Word Wheel",
        title: WORD_WHEEL_QUEST_TITLE,
        iconSrc: WORD_WHEEL_APP_ICON,
    },
];

function FacebookCoverCreative({ frameRef }) {
    const w = FACEBOOK_COVER_W;
    const h = FACEBOOK_COVER_H;
    const iconSize = 96;

    return (
        <Box
            ref={frameRef}
            sx={{
                width: w,
                height: h,
                position: "relative",
                overflow: "hidden",
                boxSizing: "border-box",
                bgcolor: "#0F172A",
            }}
        >
            {/* Featured image — nearly full-bleed */}
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    overflow: "hidden",
                    bgcolor: "#1E293B",
                }}
            >
                <Box
                    component="img"
                    src={PROMO_IMAGE}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center 38%",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(105deg, rgba(15,23,42,0.62) 0%, rgba(15,23,42,0.28) 42%, rgba(15,23,42,0.12) 68%, rgba(15,23,42,0.45) 100%)",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        left: 48,
                        right: 400,
                        bottom: 48,
                        zIndex: 1,
                    }}
                >
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: 48,
                            letterSpacing: "-0.03em",
                            color: "#FFFFFF",
                            lineHeight: 1.12,
                            textShadow: "0 2px 16px rgba(0,0,0,0.4)",
                        }}
                    >
                        Puzzle games for calm focus
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            mt: 1.25,
                            fontFamily: BRAND_STACK,
                            fontWeight: 600,
                            fontSize: 24,
                            color: "rgba(255,255,255,0.94)",
                            lineHeight: 1.3,
                            textShadow: "0 1px 10px rgba(0,0,0,0.35)",
                        }}
                    >
                        Slide tiles · piece art · spin letters
                    </Typography>
                </Box>
            </Box>

            {/* Published apps — right-bottom (away from FB profile crop) */}
            <Box
                sx={{
                    position: "absolute",
                    right: 40,
                    bottom: 32,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: "20px",
                    zIndex: 2,
                }}
            >
                {PUBLISHED_APPS.map((app) => (
                    <Box
                        key={app.key}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            width: iconSize + 16,
                        }}
                    >
                        <Box
                            sx={{
                                width: iconSize,
                                height: iconSize,
                                borderRadius: Math.round(iconSize * 0.22),
                                overflow: "hidden",
                                boxShadow: "0 10px 24px rgba(15,23,42,0.22)",
                                border: "3px solid rgba(255,255,255,0.95)",
                                bgcolor: "#FFFFFF",
                            }}
                        >
                            <Box
                                component="img"
                                src={app.iconSrc}
                                alt={app.title}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        </Box>
                        <Typography
                            component="div"
                            sx={{
                                fontFamily: BRAND_STACK,
                                fontWeight: 700,
                                fontSize: 15,
                                color: "#FFFFFF",
                                lineHeight: 1.1,
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                textShadow: "0 1px 8px rgba(0,0,0,0.55)",
                            }}
                        >
                            {app.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Company Facebook Page cover — promo visual + text, three apps bottom-right.
 */
export default function CompanyFacebookCoverPage() {
    const frameRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const previewMaxW = 820;
    const previewScale = previewMaxW / FACEBOOK_COVER_W;
    const previewH = Math.round(FACEBOOK_COVER_H * previewScale);

    const handleDownload = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                ...TO_PNG_OPTS,
                width: FACEBOOK_COVER_W,
                height: FACEBOOK_COVER_H,
                backgroundColor: "#F8FAFC",
            });
            await downloadPngDataUrl(dataUrl, FACEBOOK_COVER_FILE);
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#F1F5F9",
                py: { xs: 2, sm: 3 },
                px: { xs: 1.5, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ maxWidth: 900, mx: "auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Facebook — Company cover
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Full-bleed promo cover with the three published apps in the bottom-right (clear
                    of Facebook&apos;s profile photo crop). Recommended upload size{" "}
                    {FACEBOOK_COVER_W}×{FACEBOOK_COVER_H}.
                </Typography>

                <Box
                    sx={{
                        width: previewMaxW,
                        maxWidth: "100%",
                        height: previewH,
                        mx: "auto",
                        mb: 1.5,
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 1,
                        boxShadow: "0 6px 24px rgba(15,23,42,0.12)",
                        bgcolor: "#E2E8F0",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: FACEBOOK_COVER_W,
                            height: FACEBOOK_COVER_H,
                            transform: `scale(${previewScale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        <FacebookCoverCreative frameRef={frameRef} />
                    </Box>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveAltIcon />
                            )
                        }
                        disabled={saving}
                        onClick={handleDownload}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: "#1877F2",
                            "&:hover": { bgcolor: "#0D65D9" },
                        }}
                    >
                        Download PNG
                    </Button>
                </Stack>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", mt: 1 }}
                >
                    {FACEBOOK_COVER_W}×{FACEBOOK_COVER_H} · {FACEBOOK_COVER_FILE}
                </Typography>
                {saveError ? (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                    >
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
